import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Checkbox,
  Empty,
  Input,
  Message,
  Modal,
  Select,
  Spin,
  Tag,
  Typography,
} from '@arco-design/web-react'
import { IconDelete, IconPlus } from '@arco-design/web-react/icon'
import {
  accessTokenScope,
  createAccessTokenApi,
  deleteAccessTokenApi,
  getAccessTokenCatalogApi,
  listAccessTokensApi,
  type AccessTokenCatalog,
  type AccessTokenItem,
} from '@/api/accessTokens'
import type { SecureRunner } from '@/hooks/useSecureVerification'

interface Props {
  visible: boolean
  /** 页面级 runSecure：只在后端返回 403 时弹二次验证 */
  runSecure: SecureRunner
  onCancel: () => void
  /** 创建成功回调，参数为只显示一次的明文令牌 */
  onCreated: (token: string) => void
}

const EXPIRY_OPTIONS = [
  { label: '30 天', value: 30 },
  { label: '90 天', value: 90 },
  { label: '一年', value: 365 },
  { label: '永不过期', value: 0 },
]

/**
 * 访问令牌（v1.0.0 的 scoped access token）创建与管理。
 * 老版本 `GET /api/user/token` 已被移除，改为按权限签发。
 */
export default function AccessTokenManageModal({ visible, runSecure, onCancel, onCreated }: Props) {
  const [loading, setLoading] = useState(false)
  const [catalog, setCatalog] = useState<AccessTokenCatalog | null>(null)
  const [items, setItems] = useState<AccessTokenItem[]>([])
  const [name, setName] = useState('系统访问令牌')
  const [expiryDays, setExpiryDays] = useState<number>(30)
  const [scopes, setScopes] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)

  const defaultScopes = useMemo(() => {
    if (!catalog) return []
    // 默认只授予 personal 组（自己的资料/密钥/用量/钱包），管理类权限需要显式勾选
    return catalog.groups
      .filter((g) => g.group === 'personal')
      .flatMap((g) =>
        g.resources.flatMap((r) =>
          (r.actions ?? []).map((a) => accessTokenScope(r.resource, a.action))
        )
      )
  }, [catalog])

  const reload = async () => {
    setLoading(true)
    try {
      const [cat, list] = await Promise.all([getAccessTokenCatalogApi(), listAccessTokensApi()])
      setCatalog(cat)
      setItems(list ?? [])
      setScopes((prev) => (prev.length ? prev : []))
    } catch (err) {
      Message.error(err instanceof Error ? err.message : '加载访问令牌失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!visible) return
    setScopes([])
    void reload()
  }, [visible])

  const effectiveScopes = scopes.length ? scopes : defaultScopes

  const handleCreate = async () => {
    if (!name.trim()) {
      Message.warning('请输入名称')
      return
    }
    if (!effectiveScopes.length) {
      Message.warning('请至少选择一项权限')
      return
    }
    const sortedScopes = [...new Set(effectiveScopes)].sort()
    const expiresAt = expiryDays > 0 ? Math.floor(Date.now() / 1000) + expiryDays * 86400 : 0
    setSubmitting(true)
    try {
      const res = await runSecure(
        (proof) =>
          createAccessTokenApi(
            { name: name.trim(), scopes: sortedScopes, expires_at: expiresAt },
            proof
          ),
        {
          // 后端把 scopes + expires_at 绑进凭据，context 必须完全一致
          scope: 'access_token.generate',
          context: { scopes: sortedScopes, expires_at: expiresAt },
        }
      )
      onCreated(res.token)
      await reload()
    } catch (err) {
      if (err instanceof Error && err.message !== '用户取消了二次验证') {
        Message.error(err.message || '创建失败')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await runSecure((proof) => deleteAccessTokenApi(id, proof), {
        scope: 'access_token.revoke',
        context: { access_token_id: id },
      })
      Message.success('已删除')
      await reload()
    } catch (err) {
      if (err instanceof Error && err.message !== '用户取消了二次验证') {
        Message.error(err.message || '删除失败')
      }
    }
  }

  return (
    <Modal
      title="访问令牌"
      visible={visible}
      onCancel={onCancel}
      footer={null}
      style={{ width: 640 }}
      maskClosable={false}
    >
      {loading ? (
        <div className="py-10 flex justify-center">
          <Spin />
        </div>
      ) : (
        <div className="space-y-5">
          <div className="space-y-3 rounded-xl border border-[var(--console-border)] p-4">
            <div className="flex gap-2">
              <Input
                value={name}
                onChange={setName}
                placeholder="令牌名称，例如 监控脚本"
                maxLength={64}
              />
              <Select
                value={expiryDays}
                onChange={(v) => setExpiryDays(Number(v))}
                options={EXPIRY_OPTIONS}
                style={{ width: 140 }}
              />
            </div>

            <div className="space-y-2">
              <Typography.Text className="text-sm text-[var(--console-text-muted)]">
                权限范围（默认仅个人权限，管理类权限请按需勾选）
              </Typography.Text>
              {(catalog?.groups ?? []).map((group) => (
                <div key={group.group} className="space-y-1">
                  <Tag color={group.group === 'personal' ? 'arcoblue' : 'orange'}>
                    {group.group}
                  </Tag>
                  <Checkbox.Group
                    value={effectiveScopes}
                    onChange={(v) => setScopes(v as string[])}
                    direction="vertical"
                  >
                    {group.resources.flatMap((r) =>
                      (r.actions ?? []).map((a) => (
                        <Checkbox
                          key={`${r.resource}:${a.action}`}
                          value={accessTokenScope(r.resource, a.action)}
                        >
                          {`${r.resource}:${a.action}`}
                        </Checkbox>
                      ))
                    )}
                  </Checkbox.Group>
                </div>
              ))}
            </div>

            <Button type="primary" icon={<IconPlus />} loading={submitting} onClick={handleCreate}>
              创建令牌
            </Button>
          </div>

          <div className="space-y-2">
            <Typography.Text className="text-sm text-[var(--console-text-muted)]">
              已有令牌（{items.length}
              {catalog?.max_tokens ? ` / 上限 ${catalog.max_tokens}` : ''}）
            </Typography.Text>
            {items.length === 0 ? (
              <Empty description="还没有创建访问令牌" />
            ) : (
              items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between gap-2 rounded-lg border border-[var(--console-border)] px-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm">{item.name}</div>
                    <div className="truncate text-xs text-[var(--console-text-faint)]">
                      {(item.scopes ?? []).join(', ') || '（无权限）'}
                      {item.expires_at
                        ? ` · 到期 ${new Date(item.expires_at * 1000).toLocaleDateString()}`
                        : ' · 永不过期'}
                    </div>
                  </div>
                  <Button
                    size="mini"
                    status="danger"
                    icon={<IconDelete />}
                    onClick={() => handleDelete(item.id)}
                  />
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </Modal>
  )
}
